import React, { useState, useEffect } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import './History.css';

const History = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [modifyingBill, setModifyingBill] = useState(null);
  const [modifyData, setModifyData] = useState({});
  const [allowHistoryModification, setAllowHistoryModification] = useState(false);
  const [expandedBills, setExpandedBills] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const billsPerPage = 10;
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'confirm' });

  useEffect(() => {
    fetchTenants();
    setAllowHistoryModification(
      localStorage.getItem('allowHistoryModification') === 'true'
    );

    const handleStorageChange = () => {
      setAllowHistoryModification(
        localStorage.getItem('allowHistoryModification') === 'true'
      );
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchBillHistory();
    } else {
      setBills([]);
    }
  }, [selectedTenant]);

  const fetchTenants = async () => {
    try {
      const response = await fetch('/api/get-tenants');
      const result = await response.json();
      if (result.success) {
        setTenants(result.data);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchBillHistory = async () => {
    setLoading(true);
    setLoadingMessage('Loading bill history...');
    setError('');

    try {
      const response = await fetch(`/api/get-bill-history/${selectedTenant}`);
      const result = await response.json();

      if (result.success) {
        setBills(result.data);
      } else {
        throw new Error(result.error || 'Failed to fetch history');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `FL ${parseFloat(amount).toFixed(2)}`;
  };

  const regeneratePDF = (billId) => {
    const bill = bills.find(b => b.id === billId);
    const confirmMessage = `This will create a new PDF with updated screenshots for:\nTenant: ${selectedTenantData?.name}\nPeriod: ${bill?.billing_period}\n\nContinue?`;

    setConfirmModal({
      isOpen: true,
      title: 'Regenerate PDF?',
      message: confirmMessage,
      type: 'confirm',
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        doRegeneratePDF(billId);
      }
    });
  };

  const doRegeneratePDF = async (billId) => {
    try {
      setLoading(true);
      setLoadingMessage('Regenerating PDF with screenshots...');
      const response = await fetch(`/api/regenerate-pdf/${billId}`, {
        method: 'POST'
      });
      const result = await response.json();

      if (result.success) {
        setConfirmModal({
          isOpen: true,
          title: 'Success',
          message: 'PDF regenerated successfully!',
          type: 'alert',
          onConfirm: null
        });
        fetchBillHistory();
      } else {
        setError(result.error || 'Failed to regenerate PDF');
      }
    } catch (error) {
      setError('Network error while regenerating PDF');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const startModifyBill = (bill) => {
    setModifyingBill(bill.id);
    setModifyData({
      billing_period: bill.billing_period,
      curr_electricity_main: bill.curr_electricity_main,
      prev_electricity_main: bill.prev_electricity_main,
      curr_electricity_esm61: bill.curr_electricity_esm61 || '',
      prev_electricity_esm61: bill.prev_electricity_esm61 || '',
      curr_water: bill.curr_water,
      prev_water: bill.prev_water,
      regenerate_screenshots: false
    });
  };

  const cancelModify = () => {
    setModifyingBill(null);
    setModifyData({});
  };

  const saveModifiedBill = () => {
    const elecConsumption = parseFloat(modifyData.curr_electricity_main) - parseFloat(modifyData.prev_electricity_main);
    const waterConsumption = parseFloat(modifyData.curr_water) - parseFloat(modifyData.prev_water);

    const confirmMessage = `Period: ${modifyData.billing_period}\nNew Electricity: ${elecConsumption.toFixed(1)} kWh\nNew Water: ${waterConsumption.toFixed(1)} m\u00B3\n${modifyData.regenerate_screenshots ? '\n\u2713 Screenshots will be regenerated' : ''}\n\nProceed with modification?`;

    setConfirmModal({
      isOpen: true,
      title: 'Confirm Bill Modification',
      message: confirmMessage,
      type: 'confirm',
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        doSaveModifiedBill();
      }
    });
  };

  const doSaveModifiedBill = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Saving bill modifications...');
      const response = await fetch(`/api/modify-bill/${modifyingBill}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(modifyData)
      });

      const result = await response.json();

      if (result.success) {
        setConfirmModal({
          isOpen: true,
          title: 'Success',
          message: 'Bill modified successfully!',
          type: 'alert',
          onConfirm: null
        });
        setModifyingBill(null);
        setModifyData({});
        fetchBillHistory();
      } else {
        setError(result.error || 'Failed to modify bill');
      }
    } catch (error) {
      setError('Network error while modifying bill');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const selectedTenantData = tenants.find(t => t.id === parseInt(selectedTenant));

  const toggleBillExpanded = (billId) => {
    setExpandedBills(prev => ({
      ...prev,
      [billId]: !prev[billId]
    }));
  };

  const indexOfLastBill = currentPage * billsPerPage;
  const indexOfFirstBill = indexOfLastBill - billsPerPage;
  const currentBills = bills.slice(indexOfFirstBill, indexOfLastBill);
  const totalPages = Math.ceil(bills.length / billsPerPage);

  const changePage = (pageNumber) => {
    setCurrentPage(pageNumber);
    setExpandedBills({});
  };

  return (
    <div className="history-page">
      <h2>Billing History</h2>

      <div className="history-controls card">
        <div className="form-group">
          <label className="form-label">Select Tenant</label>
          <select
            className="form-select"
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
          >
            <option value="">Choose a tenant</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading && (
        <div className="loading-container mobile-loading">
          <span className="loading"></span>
          <span className="loading-text">{loadingMessage}</span>
        </div>
      )}

      {!loading && selectedTenant && bills.length === 0 && (
        <div className="empty-state">
          <p>No billing history found for {selectedTenantData?.name}.</p>
        </div>
      )}

      {!loading && bills.length > 0 && (
        <div className="history-content">
          <div className="history-summary card">
            <h3>Summary for {selectedTenantData?.name}</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">Total Bills</span>
                <span className="stat-value">{bills.length}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Avg. Electricity</span>
                <span className="stat-value">
                  {(bills.reduce((sum, bill) => sum + bill.electricity_consumed, 0) / bills.length).toFixed(1)} kWh
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Avg. Water</span>
                <span className="stat-value">
                  {(bills.reduce((sum, bill) => sum + bill.water_consumed, 0) / bills.length).toFixed(1)} m³
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Total Cost</span>
                <span className="stat-value">
                  {formatCurrency(bills.reduce((sum, bill) => sum + bill.electricity_cost + bill.water_cost, 0))}
                </span>
              </div>
            </div>
          </div>

          <div className="bills-list">
            {currentBills.map(bill => (
              <div key={bill.id} className={`bill-card card ${expandedBills[bill.id] ? 'expanded' : 'collapsed'}`}>
                <div
                  className="bill-header clickable"
                  onClick={() => toggleBillExpanded(bill.id)}
                >
                  <div className="bill-title">
                    <span className="expand-icon">{expandedBills[bill.id] ? '\u25BC' : '\u25B6'}</span>
                    <h4>{selectedTenantData?.name} - {bill.billing_period}</h4>
                  </div>
                  <div className="bill-meta">
                    <span className="calculation-date">{formatDate(bill.calculation_date)}</span>
                    <span className="bill-amount">FL {(bill.electricity_cost + bill.water_cost).toFixed(2)}</span>
                  </div>
                </div>

                {expandedBills[bill.id] && (
                  <div className="bill-details">
                  <div className="detail-section">
                    <h5>Electricity</h5>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span>Previous Reading:</span>
                        <span>{bill.prev_electricity_main.toFixed(1)} kWh</span>
                      </div>
                      <div className="detail-item">
                        <span>Current Reading:</span>
                        <span>{bill.curr_electricity_main.toFixed(1)} kWh</span>
                      </div>
                      {selectedTenantData?.has_esm61_meter && (
                        <>
                          <div className="detail-item">
                            <span>Previous ESM61:</span>
                            <span>{bill.prev_electricity_esm61?.toFixed(1) || 'N/A'} kWh</span>
                          </div>
                          <div className="detail-item">
                            <span>Current ESM61:</span>
                            <span>{bill.curr_electricity_esm61?.toFixed(1) || 'N/A'} kWh</span>
                          </div>
                        </>
                      )}
                      <div className="detail-item consumption">
                        <span>Consumption:</span>
                        <span>{bill.electricity_consumed.toFixed(1)} kWh</span>
                      </div>
                      <div className="detail-item cost">
                        <span>Cost:</span>
                        <span>{formatCurrency(bill.electricity_cost)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h5>Water</h5>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span>Previous Reading:</span>
                        <span>{bill.prev_water.toFixed(1)} m³</span>
                      </div>
                      <div className="detail-item">
                        <span>Current Reading:</span>
                        <span>{bill.curr_water.toFixed(1)} m³</span>
                      </div>
                      <div className="detail-item consumption">
                        <span>Consumption:</span>
                        <span>{bill.water_consumed.toFixed(1)} m³</span>
                      </div>
                      <div className="detail-item cost">
                        <span>Cost:</span>
                        <span>{formatCurrency(bill.water_cost)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bill-total">
                    <span>Total Cost:</span>
                    <span>{formatCurrency(bill.electricity_cost + bill.water_cost)}</span>
                  </div>

                  <div className="bill-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => window.open(`/api/download-pdf/${bill.id}`, '_blank')}
                  >
                    Download PDF
                  </button>

                  {allowHistoryModification && (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => regeneratePDF(bill.id)}
                        disabled={loading}
                        title="Regenerate PDF with same data"
                      >
                        Regenerate PDF
                      </button>

                      <button
                        className="btn btn-warning"
                        onClick={() => startModifyBill(bill)}
                        disabled={loading}
                        title="Modify bill data"
                      >
                        Modify Bill
                      </button>
                    </>
                  )}
                </div>

                {modifyingBill === bill.id && (
                  <div className="modify-form">
                    <h4>Modify Bill #{bill.id}</h4>

                    <div className="modify-grid">
                      <div className="form-group">
                        <label>Billing Period</label>
                        <input
                          type="text"
                          className="form-input"
                          value={modifyData.billing_period}
                          onChange={(e) => setModifyData({...modifyData, billing_period: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Previous Electricity (kWh)</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-input"
                          value={modifyData.prev_electricity_main}
                          onChange={(e) => setModifyData({...modifyData, prev_electricity_main: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Current Electricity (kWh)</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-input"
                          value={modifyData.curr_electricity_main}
                          onChange={(e) => setModifyData({...modifyData, curr_electricity_main: e.target.value})}
                        />
                      </div>

                      {selectedTenantData?.has_esm61_meter && (
                        <>
                          <div className="form-group">
                            <label>Previous ESM61 (kWh)</label>
                            <input
                              type="number"
                              step="0.1"
                              className="form-input"
                              value={modifyData.prev_electricity_esm61}
                              onChange={(e) => setModifyData({...modifyData, prev_electricity_esm61: e.target.value})}
                            />
                          </div>

                          <div className="form-group">
                            <label>Current ESM61 (kWh)</label>
                            <input
                              type="number"
                              step="0.1"
                              className="form-input"
                              value={modifyData.curr_electricity_esm61}
                              onChange={(e) => setModifyData({...modifyData, curr_electricity_esm61: e.target.value})}
                            />
                          </div>
                        </>
                      )}

                      <div className="form-group">
                        <label>Previous Water (m³)</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-input"
                          value={modifyData.prev_water}
                          onChange={(e) => setModifyData({...modifyData, prev_water: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Current Water (m³)</label>
                        <input
                          type="number"
                          step="0.1"
                          className="form-input"
                          value={modifyData.curr_water}
                          onChange={(e) => setModifyData({...modifyData, curr_water: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={modifyData.regenerate_screenshots}
                            onChange={(e) => setModifyData({...modifyData, regenerate_screenshots: e.target.checked})}
                          />
                          Regenerate screenshots
                        </label>
                      </div>
                    </div>

                    <div className="modify-actions">
                      <button
                        className="btn btn-success"
                        onClick={saveModifiedBill}
                        disabled={loading}
                      >
                        Save Changes
                      </button>

                      <button
                        className="btn btn-secondary"
                        onClick={cancelModify}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                </div>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="btn btn-secondary"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default History;
